import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Star, Quote } from 'lucide-react';

const LiveStreamTestimonials = () => {
  const testimonials = [
    {
      quote: "HiveShare Live saved our spring recital! When we had to limit in-person attendance, families could still watch their children perform. We made more money from streaming than ticket sales!",
      author: "Sarah Martinez",
      title: "Owner, Elite Dance Academy",
      location: "Austin, TX",
      revenue: "$3,200 streaming revenue vs $2,100 ticket sales"
    },
    {
      quote: "My daughter's grandparents live in Florida and haven't missed a single performance since we started using HiveShare Live. The quality is incredible and setup was so easy.",
      author: "Jennifer Chen", 
      title: "Parent, Westfield Gymnastics",
      location: "Denver, CO",
      impact: "Grandparents attended 12/12 events remotely"
    },
    {
      quote: "We stream every competition now. Parents love being able to rewatch their child's routine, and we love the additional revenue stream. It's a win-win that pays for itself!",
      author: "Mike Thompson",
      title: "Director, Champions Gymnastics",
      location: "Phoenix, AZ", 
      revenue: "$8,500 additional revenue this year"
    },
    {
      quote: "Our deployed military families can finally watch their kids perform live! HiveShare Live has connected families across the world. The emotional impact is priceless.",
      author: "Lisa Rodriguez",
      title: "Owner, Spotlight Dance Studio",
      location: "San Diego, CA",
      impact: "Connected 15 military families globally"
    },
    {
      quote: "The recording feature is genius. Families buy the live stream, then we sell the recording separately. Double revenue from every event with zero extra work!",
      author: "David Park",
      title: "Manager, Rhythm Academy",
      location: "Seattle, WA",
      revenue: "187% increase in event revenue"
    },
    {
      quote: "Setup took 8 minutes. Stream quality was flawless. Parents were texting during the show saying it looked better than being there! This is the future of recitals.",
      author: "Amanda Foster",
      title: "Director, Grace Ballet Conservatory", 
      location: "Nashville, TN",
      impact: "95% parent satisfaction rating"
    }
  ];

  const stats = [
    { number: "500+", label: "Activity Centers", subtext: "Already streaming live" },
    { number: "50,000+", label: "Families Connected", subtext: "Worldwide viewing" },
    { number: "$2.1M+", label: "Revenue Generated", subtext: "For our partners this year" },
    { number: "99.2%", label: "Uptime", subtext: "Never miss a moment" }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Trusted by Activity Centers
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Nationwide</span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-12">
            See how HiveShare Live is helping activity centers create unforgettable experiences and generate explosive revenue growth.
          </p>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-blue-600 mb-2">{stat.number}</div>
                <div className="text-lg font-semibold text-gray-900 mb-1">{stat.label}</div>
                <div className="text-sm text-gray-500">{stat.subtext}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Card className="h-full hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-blue-200 relative">
                <CardContent className="p-8">
                  {/* Quote Icon */}
                  <div className="absolute top-4 right-4 opacity-10">
                    <Quote className="h-16 w-16 text-blue-600" />
                  </div>
                  
                  {/* Star Rating */}
                  <div className="flex mb-6">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  
                  {/* Quote */}
                  <blockquote className="text-gray-700 text-lg mb-6 leading-relaxed italic">
                    "{testimonial.quote}"
                  </blockquote>
                  
                  {/* Author Info */}
                  <div className="border-t border-gray-100 pt-6">
                    <div className="font-bold text-gray-900 text-lg">{testimonial.author}</div>
                    <div className="text-blue-600 font-medium">{testimonial.title}</div>
                    <div className="text-gray-500 text-sm mb-3">{testimonial.location}</div>
                    
                    {/* Impact/Revenue Badge */}
                    {testimonial.revenue && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-4">
                        <div className="text-green-800 font-bold text-sm">💰 {testimonial.revenue}</div>
                      </div>
                    )}
                    {testimonial.impact && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                        <div className="text-blue-800 font-bold text-sm">🎯 {testimonial.impact}</div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Featured Case Study */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-white text-center max-w-5xl mx-auto"
        >
          <h3 className="text-3xl md:text-4xl font-bold mb-6">
            🏆 Featured Success Story
          </h3>
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8">
            <blockquote className="text-xl md:text-2xl font-medium mb-6 italic">
              "We went from losing money on recitals to making $15,000 profit per event. HiveShare Live didn't just solve our attendance problem - it revolutionized our business model."
            </blockquote>
            <div className="border-t border-white/20 pt-6">
              <div className="text-2xl font-bold">Rachel Kim</div>
              <div className="text-blue-200 text-lg">Owner, Starlight Dance Academy</div>
              <div className="text-blue-100">Los Angeles, CA • 450 students</div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div className="bg-white/10 rounded-xl p-4">
              <div className="text-3xl font-bold">$45K</div>
              <div className="text-blue-200">Revenue increase</div>
              <div className="text-sm text-blue-100">First year</div>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <div className="text-3xl font-bold">300%</div>
              <div className="text-blue-200">More families</div>
              <div className="text-sm text-blue-100">Watching remotely</div>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <div className="text-3xl font-bold">98%</div>
              <div className="text-blue-200">Parent satisfaction</div>
              <div className="text-sm text-blue-100">Post-event surveys</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LiveStreamTestimonials;